import { createContext, useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { DEFAULT_LANGUAGE, LANGUAGES, translations } from "./translations";

const LanguageContext = createContext(null);

export function isSupportedLanguage(language) {
    return LANGUAGES.some((item) => item.code === language);
}

export function getLanguageFromPath(pathname = "") {
    const language = pathname.split("/").filter(Boolean)[0];
    return isSupportedLanguage(language) ? language : DEFAULT_LANGUAGE;
}

export function stripLanguageFromPath(pathname = "/") {
    const parts = pathname.split("/").filter(Boolean);

    if (parts.length > 0 && isSupportedLanguage(parts[0])) {
        const stripped = `/${parts.slice(1).join("/")}`;
        return stripped === "/" ? "/" : stripped.replace(/\/+$/, "") || "/";
    }

    return pathname === "/" ? "/" : pathname.replace(/\/+$/, "") || "/";
}

export function localizedPath(path = "/", language = DEFAULT_LANGUAGE) {
    const normalizedPath = path.startsWith("/") ? path : `/${path}`;
    const cleanPath = stripLanguageFromPath(normalizedPath);
    return cleanPath === "/" ? `/${language}` : `/${language}${cleanPath}`;
}

function interpolate(value, params = {}) {
    return Object.entries(params).reduce(
        (text, [key, replacement]) => text.replaceAll(`{{${key}}}`, String(replacement)),
        value,
    );
}

function getValue(dictionary, key) {
    return key.split(".").reduce((value, part) => value?.[part], dictionary);
}

export function LanguageProvider({ children }) {
    const location = useLocation();
    const navigate = useNavigate();
    const language = getLanguageFromPath(location.pathname);
    const hasLanguagePrefix = isSupportedLanguage(location.pathname.split("/").filter(Boolean)[0]);

    useEffect(() => {
        if (hasLanguagePrefix) return;

        const cleanPath = stripLanguageFromPath(location.pathname);
        navigate(`${localizedPath(cleanPath, DEFAULT_LANGUAGE)}${location.search}${location.hash}`, { replace: true });
    }, [hasLanguagePrefix, location.hash, location.pathname, location.search, navigate]);

    useEffect(() => {
        document.documentElement.lang = LANGUAGES.find((item) => item.code === language)?.htmlLang || "ro-MD";
    }, [language]);

    const value = useMemo(() => {
        const dictionary = translations[language] || translations[DEFAULT_LANGUAGE];
        const fallbackDictionary = translations[DEFAULT_LANGUAGE];

        const t = (key, params) => {
            const translated = getValue(dictionary, key) ?? getValue(fallbackDictionary, key) ?? key;
            return typeof translated === "string" ? interpolate(translated, params) : translated;
        };

        return {
            language,
            languages: LANGUAGES,
            t,
            localizedPath: (path) => localizedPath(path, language),
            switchLanguagePath: (nextLanguage) =>
                localizedPath(stripLanguageFromPath(location.pathname), nextLanguage) + location.search + location.hash,
        };
    }, [language, location.hash, location.pathname, location.search]);

    if (!hasLanguagePrefix) {
        return null;
    }

    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
    const context = useContext(LanguageContext);

    if (!context) {
        throw new Error("useLanguage must be used inside LanguageProvider");
    }

    return context;
}

export function useTranslation() {
    return useLanguage();
}
